import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { SearchItem, SearchSources } from "@/shared/components/GlobalSearch";
import { navSections } from "@/portals/patient/layout/PatientSidebar";
import { useMedications } from "@/portals/patient/context/MedicationsContext";
import { useOptionalUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { useImagingStudies } from "@/portals/patient/context/ImagingStudiesContext";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { careTimelineQuery, careTimelineSearchQuery, reviewListQuery } from "@/portals/patient/api/patientQueries";
import { useClinicalHistory, useDiagnoses, useHealthConcerns } from "@/features/patient/clinical-history/utils/adaptClinicalHistory";
import { useDiagnosticsImaging } from "@/features/patient/diagnostics/utils/adaptDiagnostics";
import type { Note, SearchNote } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { formatDateOnly, noteTypeLabel, visitTitle } from "@/features/patient/care-treatment/utils/careTimeline";
import { recordTypeLabel } from "@/features/patient/care-treatment/utils/careNaming";
import { buildCareTeam } from "@/features/patient/care-treatment/utils/careTeamPlans";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { openSeriesViewer } from "@/features/patient/diagnostics/components/imaging/imagingNavigation";
import { ISSUE_LABELS } from "@/features/patient/medications/components/medicationReviewBadges";
import { QUEUE_PATHS, queueOf } from "@/features/patient/human-review/reviewQueues";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mar 3, 2025" from an ISO date / date-time; "" when there isn't one. */
function day(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  const date = typeof value === "number" ? new Date(value) : new Date(String(value).slice(0, 10) + "T00:00:00");
  return Number.isNaN(date.getTime()) ? "" : `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

const join = (...parts: (string | null | undefined | false)[]) => parts.filter(Boolean).join(" · ");

/** A record's page, opening that record (useSearchFocus.ts reads ?focus=). */
const focusLink = (page: string, id: string) => `${page}?focus=${encodeURIComponent(id)}`;

const CARE = "/patient/care-treatment";
/** Care & Treatment opening a note, visit, person or tab (CareTreatmentPage.tsx reads these). */
function careLink(params: Record<string, string | null | undefined>): string {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) query.set(k, v);
  return `${CARE}?${query.toString()}`;
}

/** The server searches note text only from this many letters, once typing pauses. */
const NOTE_SEARCH_MIN = 2;
const NOTE_SEARCH_DELAY_MS = 400;

/** A note's name in results: its type, else what the search answer says it is. */
function noteTitle(note: Note | undefined, hit?: SearchNote): string {
  if (note) return noteTypeLabel(note);
  return hit?.typeDisplay?.trim() || hit?.typeRaw?.trim() || "Doctor's note";
}
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

/** A review item's name: the medicine / record it's about, as its first record wrote it. */
function reviewName(fields: Record<string, unknown> | undefined): string {
  const f = fields ?? {};
  const pick = (k: string) => (typeof f[k] === "string" ? (f[k] as string).trim() : "");
  return pick("medication_name") || pick("display_name") || pick("vaccine_display") || pick("code_text");
}

/**
 * Everything the patient's header search can find: every page, and the
 * records of the profile being viewed (medicines, diagnoses, allergies,
 * procedures, vaccines, concerns, lab results, scans, uploads, review items,
 * visits, calls, doctors' notes — words inside them too, from the server's
 * note search — care team and care plans). Each result opens that record on
 * its page. Runs only while the search dropdown is open.
 */
export function usePatientSearch(query = ""): SearchSources {
  const navigate = useNavigate();
  const { activeDependent } = useFamily();
  const profileId = activeDependent?.id;

  const { medications, isLoading: medsLoading } = useMedications();
  const { diagnoses, isLoading: recordsLoading } = useDiagnoses();
  const { allergies, procedures, immunizations } = useClinicalHistory();
  const { concerns, patientConcerns } = useHealthConcerns();
  const { labResults } = useDiagnosticsImaging();
  const { data: care, isLoading: careLoading } = useQuery(careTimelineQuery(profileId));
  // Words inside notes: the server's search, once typing pauses (it's rate-limited).
  const typed = useDebouncedValue(query.trim(), NOTE_SEARCH_DELAY_MS);
  const noteQuery = typed.length >= NOTE_SEARCH_MIN ? typed : "";
  const { data: noteHits, isFetching: notesSearching } = useQuery({
    ...careTimelineSearchQuery(profileId, noteQuery),
    retry: false,
  });
  const { series } = useImagingStudies();
  const uploads = useOptionalUploadHistory();
  const { data: reviewItems = [] } = useQuery(reviewListQuery(profileId));

  const items = useMemo<SearchItem[]>(() => {
    const out: SearchItem[] = [];

    // Pages: the sidebar, plus Profile.
    for (const section of navSections) {
      for (const page of section.items) {
        out.push({ id: page.path, group: "Pages", title: page.label, subtitle: section.title, icon: page.icon, to: page.path });
      }
    }
    out.push({ id: "/patient/profile", group: "Pages", title: "Profile & Settings", subtitle: "Your details, password, SSN", icon: "lucide:user-round-cog", keywords: "account settings password ssn", to: "/patient/profile" });

    for (const m of medications) {
      out.push({
        id: m.id, group: "Medications", title: m.name, icon: "lucide:pill",
        subtitle: join(m.dosage, cap(m.status), m.prescribedBy && `by ${m.prescribedBy}`),
        keywords: join(m.genericName, m.purpose),
        to: focusLink("/patient/medications", m.id),
      });
    }
    for (const d of diagnoses) {
      out.push({
        id: d.id, group: "Diagnoses", title: d.name, icon: "lucide:stethoscope",
        subtitle: join(d.icdCode && `ICD-10 ${d.icdCode}`, cap(d.status), day(d.diagnosedDate) && `since ${day(d.diagnosedDate)}`),
        keywords: join(d.snomedCode, d.description, "condition"),
        to: focusLink("/patient/diagnosis", d.id),
      });
    }
    for (const a of allergies) {
      out.push({
        id: a.id, group: "Allergies", title: a.name, icon: "lucide:triangle-alert",
        subtitle: join(a.reaction && `Reaction: ${a.reaction}`, a.severity !== "unknown" && `${cap(a.severity)} severity`),
        keywords: join(a.category, "allergy allergic"),
        to: focusLink("/patient/complete-history", a.id),
      });
    }
    for (const c of [...concerns, ...patientConcerns]) {
      out.push({
        id: c.id, group: "Health concerns", title: c.concern, icon: "lucide:heart-plus",
        subtitle: join(cap(c.status), c.source === "patient" ? "Added by you" : "From your provider"),
        keywords: join(c.icdCode, c.category),
        to: focusLink("/patient/health-concerns", c.id),
      });
    }
    for (const p of procedures) {
      out.push({
        id: p.id, group: "Procedures", title: p.name, icon: "lucide:clipboard-plus",
        subtitle: join(day(p.date), p.performedBy && `by ${p.performedBy}`),
        keywords: join(p.code && `CPT ${p.code}`, p.reason, "surgery"),
        to: focusLink("/patient/complete-history", p.id),
      });
    }
    for (const v of immunizations) {
      out.push({
        id: v.id, group: "Immunizations", title: v.name, icon: "lucide:syringe",
        subtitle: day(v.date), keywords: "vaccine vaccination shot immunization",
        to: focusLink("/patient/complete-history", v.id),
      });
    }
    for (const l of labResults) {
      out.push({
        id: l.id, group: "Lab results", title: l.testName, icon: "lucide:flask-conical",
        subtitle: join(day(l.collectionDate || l.resultDate), l.performingLab),
        keywords: join(l.testCode, l.category, l.orderingProvider, "lab test result", ...l.results.map((r) => r.componentName)),
        to: focusLink("/patient/diagnostics", l.id),
      });
    }
    for (const s of series) {
      out.push({
        id: s.seriesInstanceUid, group: "Imaging", title: s.title || `${s.modality} scan`, icon: "lucide:scan",
        subtitle: join(s.modality, day(s.createdAt)),
        keywords: "imaging scan dicom x-ray mri ct",
        to: () => openSeriesViewer(navigate, s),
      });
    }
    for (const u of uploads?.zipUploads ?? []) {
      const name = u.title || u.fileName;
      out.push({
        id: u.id, group: "Uploads", title: name, icon: "lucide:file-text",
        subtitle: join(day(u.uploadedAt), u.fileCount ? `${u.fileCount} files` : ""),
        keywords: join(u.fileName, "upload document file"),
        to: `/patient/documents?uploadId=${encodeURIComponent(u.id)}`,
      });
    }
    for (const r of reviewItems) {
      const queue = queueOf(r);
      const label = r.empi?.identity?.uploadLabel;
      const title = queue === "identity"
        ? `Is this upload yours?${label ? ` (${label})` : ""}`
        : reviewName(r.records[0]?.fields) || ISSUE_LABELS[r.triggerType] || "Review item";
      out.push({
        id: r.id, group: "Reviews", title, icon: queue === "identity" ? "lucide:user-check" : "lucide:clipboard-check",
        subtitle: join(ISSUE_LABELS[r.triggerType], r.awaitingInput ? "Needs your answer" : ""),
        keywords: "review queue conflict identity",
        to: `${QUEUE_PATHS[queue].page}?item=${encodeURIComponent(r.id)}`,
      });
    }
    // Care & Treatment: visits, calls & letters, notes, the care team, care plans.
    const encounters = care?.encounters ?? [];
    const notes = care?.notes ?? [];
    for (const e of encounters) {
      const call = e.group === "non_visit";
      out.push({
        id: e.id, group: call ? "Calls, messages & letters" : "Visits",
        title: call ? recordTypeLabel(e) : visitTitle(e), icon: call ? "lucide:phone" : "lucide:calendar-days",
        subtitle: join(formatDateOnly(e.start), e.practitioner, e.department || e.facility),
        keywords: join(recordTypeLabel(e), e.typeRaw, e.reason, call ? "call message letter" : "encounter appointment visit"),
        to: careLink({ visit: e.id }),
      });
    }
    const noteById = new Map(notes.map((n) => [n.id, n]));
    const hitById = new Map((noteHits?.notes ?? []).map((h) => [h.id, h]));
    for (const n of notes) {
      if (hitById.has(n.id)) continue; // listed below, with the words found in it
      out.push({
        id: n.id, group: "Doctors' notes", title: noteTitle(n), icon: "lucide:file-text",
        subtitle: join(formatDateOnly(n.date), n.author), keywords: join(n.typeRaw, "note"),
        to: careLink({ note: n.id, date: n.date.slice(0, 10) }),
      });
    }
    for (const hit of noteHits?.notes ?? []) {
      const note = noteById.get(hit.id);
      out.push({
        id: hit.id, group: "Doctors' notes", title: noteTitle(note, hit), icon: "lucide:file-text",
        subtitle: join(formatDateOnly(note?.date ?? hit.date ?? undefined), note?.author ?? hit.author, `“…${hit.snippet.trim()}…”`),
        serverMatch: true,
        to: careLink({ note: hit.id, q: noteQuery, date: (note?.date ?? hit.date ?? "").slice(0, 10) }),
      });
    }
    for (const visit of noteHits?.visits ?? []) {
      const e = encounters.find((x) => x.id === visit.encounterId);
      if (!e || out.some((item) => item.id === e.id && item.serverMatch)) continue;
      out.push({
        id: `${e.id}:${visit.field}`, group: e.group === "non_visit" ? "Calls, messages & letters" : "Visits",
        title: e.group === "non_visit" ? recordTypeLabel(e) : visitTitle(e), icon: "lucide:calendar-days",
        subtitle: join(formatDateOnly(e.start), visit.snippet), serverMatch: true,
        to: careLink({ visit: e.id }),
      });
    }
    for (const person of care?.careTeam ?? buildCareTeam(encounters, notes)) {
      out.push({
        id: person.key, group: "Care team", title: person.name, icon: "lucide:user-round",
        subtitle: join(person.departments.join(", "), person.organisation),
        keywords: join(person.role, "doctor provider clinician nurse"),
        to: careLink({ person: person.key }),
      });
    }
    for (const plan of care?.carePlans ?? []) {
      out.push({
        id: plan.id, group: "Care plans", title: plan.title, icon: "lucide:list-checks",
        subtitle: join(formatDateOnly(plan.date), plan.author), keywords: join(...plan.goals, ...plan.activities, "care plan goal"),
        to: careLink({ tab: "plans" }),
      });
    }
    return out;
  }, [navigate, medications, diagnoses, allergies, concerns, patientConcerns, procedures, immunizations, labResults, series, uploads?.zipUploads, reviewItems, care, noteHits, noteQuery]);

  return { items, loading: medsLoading || recordsLoading || careLoading || notesSearching };
}
